import axios from 'axios';
import api from './axios';

const PUT_ATTEMPTS = 3;

/**
 * PUTs one file straight to B2, retrying the transient failures B2 documents.
 *
 * Measured against the live bucket: five concurrent 500KB PUTs fail roughly one
 * time in nine with `HTTP 500 <Code>InternalError</Code> internal incident`,
 * while the same uploads sent one at a time did not fail once in fourteen. B2
 * treats 5xx as retryable and expects the client to back off, so without this a
 * single unlucky request surfaced to the user as a failed upload. It showed up
 * mainly in the discussion form because that is the only one uploading several
 * files at once.
 *
 * Retrying is safe: the target is one exact key, so a repeat PUT overwrites
 * rather than duplicating, and the presigned URL stays valid for 15 minutes.
 *
 * Raw axios, NOT the shared instance: our interceptors attach an Authorization
 * header and credentials, and any header B2 did not sign breaks the signature.
 * Content-Type must match what was signed, exactly.
 *
 * Exported so the case-media helper in features/cases/api/media-api.js can use
 * the same policy. It presigns through a different endpoint and so cannot reuse
 * uploadFile, but the retry behaviour should not diverge between them.
 */
export const putToStorage = async (uploadUrl, file, onProgress) => {
  let lastError;

  for (let attempt = 1; attempt <= PUT_ATTEMPTS; attempt += 1) {
    try {
      return await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      });
    } catch (err) {
      lastError = err;

      // 4xx is the request itself being wrong — an expired signature, a key we
      // are not allowed to write. Retrying sends identical bytes to an
      // identical URL and fails identically, so fail fast instead.
      const status = err?.response?.status;
      if (status && status < 500) throw err;
      if (attempt === PUT_ATTEMPTS) break;

      // Rewind the bar rather than leaving it stuck at the point it died.
      if (onProgress) onProgress(0);
      // Jittered backoff, so files that started together do not retry together
      // and recreate the burst that triggered the fault.
      const backoff = 400 * 2 ** (attempt - 1) + Math.random() * 300;
      await new Promise((resolve) => setTimeout(resolve, backoff));
    }
  }

  throw lastError;
};

/**
 * Uploads one file to the staging prefix and returns its STAGING key.
 *
 * This is the only general upload helper in the app — do not add another.
 *
 * The file goes straight from the browser to Backblaze B2 and never passes
 * through our backend. Two steps here, of which only the second carries bytes:
 *
 *   1. ask our API to sign an upload URL for a key it chooses
 *   2. PUT the file to that URL — direct to B2
 *
 * Step 1 is a small JSON request through the shared axios instance, so it
 * inherits the bearer token, the CSRF header and the silent 401-refresh retry.
 * Step 2 deliberately does not — see the note on the PUT below.
 *
 * **The returned key is a `tmp/` key and is NOT permanent.** Call
 * `confirmUpload()` with it when the form is submitted; that is what verifies
 * the bytes and promotes the object to its permanent key.
 *
 * This split is deliberate. Confirming here — the moment the transfer finished —
 * promoted the object out of `tmp/` before the user had decided to submit
 * anything, so abandoning the form stranded a file at a permanent key that no
 * lifecycle rule can reach. Measured: 8 orphans from one evening of testing,
 * against zero on the case-media path, which has always confirmed at submit.
 * Résumés and case responses were among them, which makes it a personal-data
 * question rather than a storage one.
 *
 * Left in `tmp/`, an abandoned upload is swept by the bucket lifecycle rule and
 * costs a day of storage. Nothing has to be cleaned up by hand.
 *
 * @param {File} file
 * @param {object} options
 * @param {'resume'|'case-response'|'case-image'|'discussion-image'|'opportunity-attachment'} options.purpose
 *   Tells the server which prefix the file belongs under. The server owns the
 *   mapping; an unknown value is rejected with 400.
 * @param {(percent:number)=>void} [options.onProgress]
 * @returns {Promise<{key: string}>} the staging key to hold and later confirm
 */
export const uploadFile = async (file, { purpose, onProgress } = {}) => {
  if (!purpose) throw new Error('uploadFile requires a purpose');

  const { data: presigned } = await api.post('/upload/presign', {
    purpose,
    filename: file.name,
    contentType: file.type,
    size: file.size,
  });

  const { key, uploadUrl } = presigned?.data ?? {};
  if (!uploadUrl) throw new Error('Could not get an upload URL. Please try again.');

  await putToStorage(uploadUrl, file, onProgress);

  return { key };
};

/**
 * Promotes a staged upload to its permanent key. Call this at submit time, for
 * every staging key the form is about to send.
 *
 * The server re-reads the first few KB from B2 and rejects anything whose bytes
 * do not match the type it claimed, anything oversized, and any key that is not
 * the caller's own — so this is the point at which an upload can still fail, and
 * the caller must not submit if it throws.
 *
 * **Idempotent by design.** A key that is already permanent is returned
 * unchanged rather than re-confirmed. Without this, a submit that confirmed
 * successfully and then failed to save the record would break on retry: the
 * second confirm would be handed a `public/…` key and rejected for not matching
 * the `tmp/` prefix the server pins.
 *
 * @param {string} key staging key from uploadFile
 * @param {string} purpose the same purpose the file was uploaded with
 * @returns {Promise<{key: string, url: string|null, visibility: 'public'|'private'}>}
 *   `key` is the permanent key to persist. `url` is a short-lived signed link,
 *   null for private purposes so a private key never becomes fetchable.
 */
export const confirmUpload = async (key, purpose) => {
  if (!key) throw new Error('confirmUpload requires a key');
  if (!key.startsWith('tmp/')) return { key, url: null, visibility: null };

  const { data: confirmed } = await api.post('/upload/confirm', { key, purpose });
  return confirmed.data;
};

/**
 * Uploads a registration document (college ID or veterinary licence) to B2 and
 * returns its staging key.
 *
 * Same direct-to-B2 approach as uploadFile, but these live in the auth service
 * and are confirmed by the request that writes the profile row rather than by a
 * separate call — so the upload and the database write stay in one request and
 * a failed write can still clean up after itself.
 *
 * The returned key is NOT a permanent one. Send it as `collegeIdKey` on
 * POST /students/profile, or as `key` on POST /students/profile/veterinarian-license.
 *
 * @param {File} file
 * @param {'collegeId'|'license'} documentType
 * @returns {Promise<string>} the staging key to submit
 */
export const uploadRegistrationDocument = async (file, documentType) => {
  const { data: presigned } = await api.post('/students/profile/presign', {
    documentType,
    filename: file.name,
    contentType: file.type,
    size: file.size,
  });

  const { key, uploadUrl } = presigned?.data ?? {};
  if (!uploadUrl) throw new Error('Could not get an upload URL. Please try again.');

  await putToStorage(uploadUrl, file);

  return key;
};

/**
 * Uploads a profile photo to B2 and returns its staging key.
 *
 * Send the key as `avatarUrl` on PATCH /users/me. The server verifies the bytes
 * and moves the object to its permanent key in the same request, so a failed
 * save cannot leave a stray photo behind.
 *
 * Avatars are public — they render in `<img>` beside every post — so reads come
 * back as signed URLs rather than being streamed through the backend.
 *
 * @param {Blob|File} file - compressed image blob
 * @returns {Promise<string>} the staging key to submit
 */
export const uploadAvatar = async (file) => {
  const { data: presigned } = await api.post('/students/profile/presign', {
    documentType: 'avatar',
    // A compressed Blob has no name of its own; the server ignores it anyway and
    // takes the extension from the content type.
    filename: file.name || 'avatar.jpg',
    contentType: file.type,
    size: file.size,
  });

  const { key, uploadUrl } = presigned?.data ?? {};
  if (!uploadUrl) throw new Error('Could not get an upload URL. Please try again.');

  await putToStorage(uploadUrl, file);

  return key;
};
